// Golden set CSV checks run in the browser before upload, so a user sees every
// problem with its row number. Core makes the final check on upload. Rules:
// openspec/changes/evaluation-run-setup/specs/golden-set-library and
// .../golden-set-dialogue-order.

export const REQUIRED_COLUMNS = ["script", "question"];
export const OPTIONAL_COLUMNS = ["meaning", "meaning2", "meaning3", "line_order"];
const MEANING_COLUMNS = ["meaning", "meaning2", "meaning3"];
// Core's label for "no match expected" (convovec-core app/services/convo_eval.py).
const NO_MATCH_LABEL = "not understood.";

export const SAMPLE_CSV = `script,question,meaning,meaning2,meaning3,line_order
tina_jones_bsn_healthhistory,0-10 how bad is the pain,how I would rate my pain on a scale,,how severe my pain is,1
tina_jones_bsn_healthhistory,After you scraped it did you treat it or see someone,if I did anything to treat my foot at home,what else I do for my wound,,2
tina_jones_bsn_healthhistory,Alcohol,if I drink alcohol,,,3
tina_jones_bsn_healthhistory,All right goodbye,goodbye and to have a good day,,to say goodbye,4
tina_jones_bsn_healthhistory,What is your name,,,,5
`;

export const downloadSampleCsv = () => {
  const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = "sample_eval.csv";
  a.click();
  URL.revokeObjectURL(url);
};

// RFC 4180-style parse: quoted fields may hold commas, quotes ("") and line breaks.
export const parseCsv = (text) => {
  const rows = [];
  let row = [];
  let field = "";
  let quoted = false;
  for (let i = 0; i < text.length; i += 1) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') {
        field += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === ",") {
      row.push(field);
      field = "";
    } else if (ch === "\n" || ch === "\r") {
      if (ch === "\r" && text[i + 1] === "\n") i += 1;
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
    } else {
      field += ch;
    }
  }
  if (field || row.length) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((cell) => cell.trim()));
};

const checkLineOrder = (records, errors, warnings) => {
  const seen = {};
  records.forEach(({ rowNumber, script, values }) => {
    const raw = (values.line_order || "").trim();
    if (!/^\d+$/.test(raw) || Number(raw) < 1) {
      errors.push({ row: rowNumber, script, message: `line_order "${raw}" must be a whole number of 1 or more` });
      return;
    }
    const key = `${script}\u0000${Number(raw)}`;
    if (seen[key]) {
      errors.push({
        row: rowNumber,
        script,
        message: `line_order ${raw} is also on row ${seen[key]} of script ${script}`,
      });
    } else {
      seen[key] = rowNumber;
    }
  });

  const byScript = {};
  records.forEach(({ script, values }) => {
    const n = Number((values.line_order || "").trim());
    if (Number.isInteger(n) && n >= 1) (byScript[script] ||= new Set()).add(n);
  });
  Object.entries(byScript).forEach(([script, numbers]) => {
    const max = Math.max(...numbers);
    for (let n = 1; n <= max; n += 1) {
      if (!numbers.has(n)) warnings.push({ script, message: `Script ${script} has no line ${n}` });
    }
  });
};

export const checkGoldenSetCsv = (text) => {
  const errors = [];
  const warnings = [];
  const [header = [], ...body] = parseCsv(text || "");
  const columns = header.map((h) => h.trim());

  REQUIRED_COLUMNS.filter((c) => !columns.includes(c)).forEach((c) =>
    errors.push({ row: 1, script: null, message: `Missing required column "${c}"` }),
  );
  const hasLineOrder = columns.includes("line_order");
  if (errors.length) {
    return { ok: false, rows: 0, scripts: 0, noExpectedBucket: 0, hasLineOrder, errors, warnings };
  }

  // Row numbers count the header as row 1, as a spreadsheet shows them.
  const records = body.map((cells, i) => {
    const values = Object.fromEntries(columns.map((c, j) => [c, cells[j] ?? ""]));
    return { rowNumber: i + 2, script: values.script.trim(), values };
  });
  records.forEach(({ rowNumber, script, values }) => {
    if (!script || !values.question.trim()) {
      errors.push({ row: rowNumber, script: script || null, message: "Every row needs a script and a question" });
    }
  });
  if (hasLineOrder) checkLineOrder(records, errors, warnings);

  const noExpectedBucket = records.filter(({ values }) =>
    MEANING_COLUMNS.every((c) => {
      const v = (values[c] || "").trim().toLowerCase();
      return !v || v === NO_MATCH_LABEL;
    }),
  ).length;

  return {
    ok: errors.length === 0,
    rows: records.length,
    scripts: new Set(records.map((r) => r.script).filter(Boolean)).size,
    noExpectedBucket,
    hasLineOrder,
    errors,
    warnings,
  };
};
